'use client'

import type { KeyboardEvent, ReactNode } from 'react'

import { Button, Icon, PropertiesTag } from '@graftcode/design-system'

import { getBadgeType } from '@/core/utils/getBadgeType'
import { getPropertiesTagVariant } from '@/core/utils/getPropertiesTagVariant'

import styles from './MethodDetail.module.scss'

interface MethodDetailProps {
  methodName: string
  badge?: string
  content: ReactNode
  currentIndex: number
  totalCount: number
  onBack: () => void
  onPrevious?: () => void
  onNext?: () => void
}

export function MethodDetail({
  methodName,
  badge,
  content,
  currentIndex,
  totalCount,
  onBack,
  onPrevious,
  onNext,
}: MethodDetailProps) {
  const badgeVariant = getPropertiesTagVariant(getBadgeType(badge))
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < totalCount - 1
  const handleAppBarKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onBack()
    }
  }

  return (
    <div className={styles.mobileMethodDetail}>
      <div
        className={styles.appBar}
        role="button"
        tabIndex={0}
        aria-label="Go back"
        onClick={onBack}
        onKeyDown={handleAppBarKeyDown}
      >
        <Button iconOnly size="l" variant="ghost" icon={Icon.ChevronLeftRegular} />
        <span className={styles.appBarTitle}>{methodName}</span>
      </div>

      <div className={styles.scrollContent}>
        <div className={styles.detailSections}>
          {badge && (
            <div className={styles.section}>
              <span className={styles.sectionTitle}>Type</span>
              <PropertiesTag variant={badgeVariant} withoutIcon>
                {badge}
              </PropertiesTag>
            </div>
          )}

          {content}
        </div>
      </div>

      {totalCount > 1 && (
        <div className={styles.footer}>
          <Button variant="outlined" size="m" onClick={onPrevious} disabled={!hasPrevious}>
            Previous
          </Button>
          <span className={styles.counter}>
            {currentIndex + 1}/{totalCount}
          </span>
          <Button variant="primary" size="m" onClick={onNext} disabled={!hasNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
